import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Calendar, Activity, TrendingUp, ChevronDown, Filter, X, SlidersHorizontal, Loader2 } from 'lucide-react'
import { pointsApi } from '../../lib/api'
import { usePageTitle } from '../../hooks/usePageTitle'

interface PointRecord {
  id: string
  eventType: string
  points: number
  status: string
  createdAt: string
  hospital: string
  cancelReason?: string
  remark?: string
}

const PAGE_SIZE = 20

const UserPoints = () => {
  usePageTitle()
  const navigate = useNavigate()
  const [user, setUser] = useState<any>(null)
  const [records, setRecords] = useState<PointRecord[]>([])
  const [allRecords, setAllRecords] = useState<PointRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [eventTypeFilter, setEventTypeFilter] = useState('all')
  const [hasActiveFilter, setHasActiveFilter] = useState(false)
  const [eventTypes, setEventTypes] = useState<string[]>(['全部'])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [stats, setStats] = useState({ totalPoints: 0, monthPoints: 0, pendingPoints: 0 })

  // 登录守卫
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/user/login')
    }
  }, [navigate])

  // 从 localStorage 拿用户信息
  useEffect(() => {
    window.scrollTo(0, 0)
    const stored = localStorage.getItem('userAccount')
    if (stored) {
      try { setUser(JSON.parse(stored)) } catch { /* ignore */ }
    }
  }, [])

  // 加载积分记录
  const loadRecords = useCallback(async (page: number = 1, resetPage = false) => {
    if (resetPage) { setCurrentPage(1); setRecords([]) } else { setCurrentPage(page) }
    setLoading(true)
    try {
      const params: any = { page, page_size: PAGE_SIZE }
      if (selectedFilter !== 'all') params.status = selectedFilter
      if (dateRange.start) params.start_date = dateRange.start
      if (dateRange.end) params.end_date = dateRange.end

      // 传入当前用户 ID，确保只展示当前用户的积分记录
      const stored = localStorage.getItem('userAccount')
      if (stored) {
        try {
          const currentUser = JSON.parse(stored)
          if (currentUser?.id) params.user_id = currentUser.id
        } catch { /* ignore */ }
      }

      const resp = await pointsApi.records(params) as any
      const items = resp.records || []
      const total = resp.total || items.length

      // 保存统计数据
      setStats({
        totalPoints: resp.total_confirmed || 0,
        monthPoints: resp.month_confirmed || 0,
        pendingPoints: resp.pending_points || 0,
      })

      const mapped: PointRecord[] = items.map((r: any) => ({
        id: r.id,
        eventType: r.event_type?.name || r.event_type_name || '未知',
        points: r.points,
        status: r.status,
        createdAt: r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : '',
        hospital: r.hospital_name || '',
        cancelReason: r.cancel_reason || '',
        remark: r.remark || '',
      }))

      // 提取所有事件类型供筛选用
      const types = ['全部', ...new Set(mapped.map(r => r.eventType))]
      setEventTypes(types)

      if (resetPage) {
        setAllRecords(mapped)
        setRecords(mapped)
      } else {
        setAllRecords(prev => [...prev, ...mapped])
        setRecords(prev => [...prev, ...mapped])
      }
      const newTotal = resetPage ? mapped.length : allRecords.length + mapped.length
      const totalPages = resp.total_pages || 1
      setHasMore(page < totalPages)
    } catch (e) {
      console.error('Failed to load records', e)
    } finally {
      setLoading(false)
    }
  }, [selectedFilter, dateRange])

  useEffect(() => { loadRecords(1, true) }, [selectedFilter, dateRange])

  // 本地事件类型筛选（因为后端不支持按 name 过滤）
  const filteredRecords = eventTypeFilter === 'all'
    ? records
    : records.filter(r => r.eventType === eventTypeFilter)

  // 按月份分组
  const groupedRecords: { [key: string]: PointRecord[] } = {}
  filteredRecords.forEach((record) => {
    const month = record.createdAt.split(' ')[0]
    if (!groupedRecords[month]) groupedRecords[month] = []
    groupedRecords[month].push(record)
  })

  // 统计（从API返回获取）
  const totalPoints = stats.totalPoints
  const monthPoints = stats.monthPoints
  const pendingPoints = stats.pendingPoints

  const clearFilters = () => {
    setSelectedFilter('all'); setEventTypeFilter('all')
    setDateRange({ start: '', end: '' }); setHasActiveFilter(false)
  }

  const applyFilters = () => {
    const hasFilters = selectedFilter !== 'all' || eventTypeFilter !== 'all' || dateRange.start || dateRange.end
    setHasActiveFilter(hasFilters)
    setShowFilterModal(false)
  }

  const userPoints = user?.points ?? 0

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 积分概览 */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-8">
        <div className="text-center text-white">
          <p className="text-green-100">当前积分</p>
          <p className="text-5xl font-bold mt-2">
            {loading && records.length === 0 ? <Loader2 className="w-10 h-10 animate-spin mx-auto" /> : userPoints}
          </p>
          <p className="text-green-200 text-sm mt-1">分</p>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white/80 text-xs">累计获得</p>
            <p className="text-white font-bold mt-1">{totalPoints}</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white/80 text-xs">本月获得</p>
            <p className="text-white font-bold mt-1">{monthPoints}</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
            <p className="text-white/80 text-xs">待确认</p>
            <p className="text-white font-bold mt-1">{pendingPoints}</p>
          </div>
        </div>
      </div>

      {/* 状态筛选标签 */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-md p-2 flex space-x-2">
          {[
            { key: 'all', label: '全部' },
            { key: 'confirmed', label: '已获得' },
            { key: 'pending', label: '待确认' },
            { key: 'cancelled', label: '已撤销' }
          ].map((filter) => (
            <button key={filter.key}
              onClick={() => setSelectedFilter(filter.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === filter.key ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* 高级筛选按钮 */}
      <div className="px-4 mt-2">
        <button onClick={() => setShowFilterModal(true)}
          className={`w-full py-3 rounded-xl border flex items-center justify-center space-x-2 transition-colors ${
            hasActiveFilter ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}>
          <SlidersHorizontal className="w-4 h-4" />
          <span className="text-sm font-medium">{hasActiveFilter ? '已设置筛选条件' : '更多筛选条件'}</span>
          {hasActiveFilter && (
            <button onClick={(e) => { e.stopPropagation(); clearFilters() }} className="ml-2 p-1 bg-green-100 rounded-full hover:bg-green-200">
              <X className="w-3 h-3" />
            </button>
          )}
        </button>
      </div>

      {/* 加载状态 */}
      {loading && records.length === 0 && (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
      )}

      {/* 积分记录列表 */}
      <div className="px-4 mt-4 space-y-4">
        {Object.entries(groupedRecords).map(([month, monthRecords]) => (
          <div key={month}>
            <div className="flex items-center space-x-2 mb-3">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">{month}</span>
            </div>
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              {monthRecords.map((record, index) => (
                <div key={record.id}
                  className={`p-4 flex items-center ${index < monthRecords.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                    record.status === 'confirmed' ? 'bg-green-100' : record.status === 'pending' ? 'bg-orange-100' : 'bg-red-100'
                  }`}>
                    <Activity className={`w-5 h-5 ${
                      record.status === 'confirmed' ? 'text-green-600' : record.status === 'pending' ? 'text-orange-600' : 'text-red-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-800">{record.eventType}</p>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        record.status === 'confirmed' ? 'bg-green-100 text-green-700'
                          : record.status === 'pending' ? 'bg-orange-100 text-orange-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {record.status === 'confirmed' ? '已确认' : record.status === 'pending' ? '待确认' : '已撤销'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{record.createdAt} · {record.hospital}</p>
                    {record.cancelReason && (
                      <p className="text-xs text-red-500 mt-1">撤销原因：{record.cancelReason}</p>
                    )}
                    {record.remark && (
                      <p className="text-xs text-gray-400 mt-1">备注：{record.remark}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${record.status === 'cancelled' ? 'text-red-500' : 'text-green-600'}`}>
                      {record.status === 'cancelled' ? '' : (record.points > 0 ? '+' : '')}{record.points}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* 加载更多 */}
        {hasMore && !loading && (
          <button onClick={() => loadRecords(currentPage + 1)}
            className="w-full py-3 text-center text-green-600 text-sm hover:underline">
            加载更多
          </button>
        )}

        {filteredRecords.length === 0 && !loading && (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">暂无积分记录</p>
            <p className="text-sm text-gray-400 mt-1">参与健康活动即可获得积分哦~</p>
          </div>
        )}
      </div>

      {/* 筛选弹窗 */}
      {showFilterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800 flex items-center">
                <Filter className="w-5 h-5 mr-2" />筛选条件
              </h3>
              <button onClick={() => setShowFilterModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* 时间范围筛选 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Calendar className="w-4 h-4 inline mr-2" />时间范围
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">开始日期</label>
                    <input type="date" value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">结束日期</label>
                    <input type="date" value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  {['近一周', '近一月', '近三月'].map((label, index) => (
                    <button key={label}
                      onClick={() => {
                        const now = new Date(); let startDate = new Date()
                        if (index === 0) startDate.setDate(now.getDate() - 7)
                        else if (index === 1) startDate.setMonth(now.getMonth() - 1)
                        else startDate.setMonth(now.getMonth() - 3)
                        setDateRange({ start: startDate.toISOString().split('T')[0], end: new Date().toISOString().split('T')[0] })
                      }}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 事件类型筛选 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Activity className="w-4 h-4 inline mr-2" />事件类型
                </label>
                <div className="flex flex-wrap gap-2">
                  {eventTypes.map((type) => (
                    <button key={type}
                      onClick={() => setEventTypeFilter(type === '全部' ? 'all' : type)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        (type === '全部' ? 'all' : type) === eventTypeFilter
                          ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 px-6 py-4 border-t">
              <button onClick={clearFilters}
                className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">清除筛选</button>
              <button onClick={applyFilters}
                className="flex-1 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors">应用筛选</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserPoints
